import React, { Component, createRef } from 'react';
import classnames from 'classnames';
import { SortableElement, SortableContainer, SortEnd, SortEndHandler, SortEvent } from 'react-sortable-hoc';
import { Theme } from '@/sunzi';
import { fileToBase64 } from '@/utils';
import { Icon } from '..';
import styles from './style.less';

interface SortableListProps {
  /** 主题 */
  theme: Theme;
	/** 数据源 */
	dataSource: string[];
	/** 选择图片 */
	onCameraChange(base64: string[]): void;
	/** 外置更换图片方法 */
  onExternalCameraChange?: () => Promise<string>;
	/** 是否显示添加图片按钮 */
	showAddButton: boolean;
	/** 排序结束 */
	onSortEnd: SortEndHandler;
	/** 禁止拖拽 */
	disabled?: boolean;
	/** 支持传多图 */
	multiple?: boolean;
	/** 激活下标 */
	activeIndex?: number;
	/** 排序改变 */
	onSortChange?: (index: number) => void;
}

interface SortableListState {}

const SortableListItem = SortableElement(({
	source,
	sortableIndex,
	onClick,
	activeIndex,
}: {
	source: string;
	sortableIndex: number;
	activeIndex?: number;
	onClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
}) => (
	<div
		onClick={onClick}
		data-index={sortableIndex}
		className={classnames(styles.sortableItem, {
			[styles.sortableItemActive]: activeIndex === sortableIndex
		})}
	>
		<div className={styles.sortableItemHeader}>
			<div className={styles.sortableItemIndex}>{sortableIndex + 1}</div>
		</div>
		<div className={styles.sortableItemContent}>
			<img src={source} />
		</div>
	</div>
));

class _SortableList extends Component<{
	refWrapper: React.RefObject<HTMLDivElement>;
	className?: string;
	dataSource: string[],
	activeIndex?: number;
	disabled?: boolean;
	showAddButton: boolean;
	multiple?: boolean;
	onExternalCameraChange?: () => Promise<string>;
	onItemClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
	onCameraChange(base64: string[]): void;
}> {
	private _input: React.RefObject<HTMLInputElement> = createRef();

	/** 外置更换文件监听方法 */
	onExternalCameraChange = async () => {
		const { onExternalCameraChange, onCameraChange } = this.props;
    if (onExternalCameraChange) {
      const base64 = await onExternalCameraChange();
      onCameraChange([ base64 ]);
    }
	}

	/** 更换文件监听 */
	onCameraChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const _fileToBase64 = (file: Blob) => new Promise<string>(resolve => {
			fileToBase64(file, (base64: string) => {
        resolve(base64);
      });
		});
		const files = e.target.files;
		if (files) {
			const images = await Promise.all<string>(
				Array.from({ length: files.length || 0 })
					.map((item, index) => _fileToBase64(files[index]))
			);
			this.props.onCameraChange(images);
		}
	}

	render() {
		const { className, refWrapper, dataSource, disabled, activeIndex, onItemClick, showAddButton,
			onExternalCameraChange, multiple } = this.props;
		return (
			<div ref={refWrapper} className={className}>
				{dataSource.map((item, index) =>
					<SortableListItem
						key={index}
						source={item}
						index={index}
						sortableIndex={index}
						disabled={disabled}
						activeIndex={activeIndex}
						onClick={onItemClick}
					/>
				)}
				{showAddButton &&
					<div className={classnames(styles.sortableItem, styles.sortableItemAdd)}>
						<div className={styles.sortableItemContent} onClick={this.onExternalCameraChange}>
							<Icon type="add" size={20} color="#fff" />
							{!onExternalCameraChange &&
								<input
									ref={this._input}
									type="file"
									accept="image/*"
									multiple={multiple}
									onChange={this.onCameraChange}
								/>
							}
						</div>
					</div>
				}
			</div>
		)
	}
}

const SortableList = SortableContainer(_SortableList);

class SortableComponent extends Component<SortableListProps, SortableListState> {
	private _wrapper: React.RefObject<HTMLDivElement> = createRef();

	/** 排序结束 */
	onSortEnd = (sort: SortEnd, event: SortEvent) => {
		const { onSortChange, onSortEnd } = this.props;
		onSortEnd(sort, event);
		onSortChange && onSortChange(sort.newIndex);
	}

  /** 点击排序项 */
	onSortableItemClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		const { onSortChange } = this.props;
		const index: string | undefined = event.currentTarget.dataset['index'];
		index && onSortChange && onSortChange(parseInt(index));
	}

	/** 滚动到最底部 */
	scrollToLeft = () => {
		if (this._wrapper.current)
			this._wrapper.current.scrollLeft = this._wrapper.current.scrollWidth;
	}

	render() {
		const { theme, dataSource, showAddButton, disabled, activeIndex, multiple, onCameraChange, onExternalCameraChange } = this.props;

		return (
			<SortableList
				refWrapper={this._wrapper}
				className={classnames(styles.sortableList, {
					[styles.sortableListLight]: theme === Theme.light
				})}
				axis="x"
				lockAxis="x"
				pressDelay={150}
				helperClass={styles.sortableHelper}
				dataSource={dataSource}
				onSortEnd={this.onSortEnd}
				activeIndex={activeIndex}
				multiple={multiple}
				disabled={disabled}
				showAddButton={showAddButton}
				onItemClick={this.onSortableItemClick}
				onCameraChange={onCameraChange}
				onExternalCameraChange={onExternalCameraChange}
			/>
		);
	}
}

export default SortableComponent;
